import { CloseIcon } from '@chakra-ui/icons'
import { Divider, Flex, Text, useDisclosure } from '@chakra-ui/react'
import { useDeleteDocuments } from 'api/vendor-projects'
import { ConfirmationBox } from 'components/Confirmation'
import FileIcon from 'icons/file-icon'
import numeral from 'numeral'
import { useState } from 'react'
import Dropzone from 'react-dropzone'
import { BiDownload, BiImport } from 'react-icons/bi'
import { downloadFileOnly } from 'utils/file-utils'

const getFileSize = byte => {
  const sizeInMB = Number(byte / 1000000)
  return numeral(sizeInMB).format('0,0.00')
}

type FileDragDropProps = {
  onUpload: (files) => void
  uploadedFiles?: any[] | null | undefined
  documentType?: string
  isRequired?: boolean | undefined | null
  testId?: string
  name?: string
}

export default function FileDragDrop({ onUpload, uploadedFiles, documentType, ...fieldProps }: FileDragDropProps) {
  const [files, setFiles] = useState<any>(uploadedFiles ?? [])

  const handleDrop = acceptedFiles => {
    // Filtering corrupted files (with file size 0)
    const filteredDocs = acceptedFiles?.filter(f => !!f.size)
    setFiles([...files, ...filteredDocs.map(file => file)])
    const documents = [...files, ...filteredDocs.map(file => file)] //formatDocuments(acceptedFiles);
    onUpload(documents)
  }

  const handleDelete = index => {
    files.splice(index, 1)
    setFiles([...files])
    onUpload([...files])
  }

  return (
    <Flex width="100%" direction="column" gap="30px" mt={4}>
      <Dropzone
        accept={{
          ...(documentType === 'images' && { 'image/png': ['.png'], 'image/jpg': ['.jpg'], 'image/jpeg': ['.jpeg'] }),
        }}
        onDrop={handleDrop}
        {...fieldProps}
      >
        {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open }) => {
          const additionalClass = isDragAccept ? 'accept' : isDragReject ? 'reject' : ''

          return (
            <Flex
              data-testid="drop-zone"
              alignItems="center"
              justifyContent="center"
              {...getRootProps({
                className: `${additionalClass}`,
              })}
              style={{
                width: '100%',
                height: '115px',
                border: '2px dashed #A0AEC0',
                borderRadius: '15px',
                backgroundColor: '#F7FAFC',
              }}
              onClick={e => e.stopPropagation()}
            >
              <input {...getInputProps()} data-testid="chooseFile" />
              <Flex
                direction="column"
                gap="10px"
                alignItems="center"
                style={{
                  fontSize: '14px',
                  lineHeight: '24px',
                  fontWeight: 'bold',
                  color: '#4A5568',
                }}
              >
                <BiImport style={{ width: '24px', height: '24px', color: '#718096' }} />
                {isDragActive ? (
                  <Text as="span">Drop Here</Text>
                ) : (
                  <>
                    <Text as="span">Drag & Drop</Text>
                    <Text as="span" color="#718096">
                      Or{' '}
                      <Text as="span" onClick={open} color="#4E87F8" cursor="pointer">
                        Choose
                      </Text>{' '}
                      your files.
                    </Text>
                  </>
                )}
              </Flex>
            </Flex>
          )
        }}
      </Dropzone>
      <UploadedFiles files={files} handleDelete={handleDelete} />
    </Flex>
  )
}

function UploadedFiles({ files, handleDelete }) {
  const {
    isOpen: isDeleteConfirmationOpen,
    onClose: onDeleteConfirmatonClose,
    onOpen: onDeleteConfirmationOpen,
  } = useDisclosure()
  const { mutate: deleteDocuments, isLoading } = useDeleteDocuments()
  const [deletedItem, setDeletedItem] = useState<any>()

  const deleteDocument = index => {
    deleteDocuments(deletedItem?.file?.id, {
      onSuccess() {
        handleDelete(index)
        onDeleteConfirmatonClose()
      },
    })
  }

  if (files?.length > 0) {
    return (
      <Flex direction="column" gap="20px" maxH={'200px'} overflowY={'scroll'}>
        <Flex alignItems="center">
          <Text width="150px" color="#4A5568" fontSize="14px" fontWeight="500" lineHeight="20px">
            Uploaded Files
          </Text>
          <Divider
            orientation="horizontal"
            style={{
              height: '1px',
              backgroundColor: '#CBD5E0',
            }}
          />
        </Flex>
        <Flex direction="column" gap="8px">
          {files.map((file, index) => (
            <Flex
              key={`${file.name}-${index}`}
              alignItems="center"
              justifyContent="space-between"
              style={{
                backgroundColor: '#F7FAFE',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                maxWidth: '342px',
                maxHeight: '35px',
                padding: '5px 10px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '24px',
                color: '#718096',
              }}
            >
              <Flex gap="10px" alignItems="center">
                <FileIcon />
                <Text
                  as="span"
                  noOfLines={[1]}
                  style={{
                    wordBreak: 'break-all',
                    width: '150px',
                  }}
                >
                  {file.s3Url ? file.fileType : file.name}
                </Text>
              </Flex>
              <Text as="span">{getFileSize(file.size)} MB</Text>
              {!!file?.s3Url && (
                <BiDownload
                  fontSize="sm"
                  cursor={'pointer'}
                  onClick={() => {
                    downloadFileOnly(file)
                  }}
                />
              )}
              <CloseIcon
                data-testid={'delete-' + index}
                cursor="pointer"
                width="7.42px"
                height="7.42px"
                color="#4A5568"
                onClick={() => {
                  onDeleteConfirmationOpen()
                  setDeletedItem({ index, file })
                }}
              />
            </Flex>
          ))}
        </Flex>
        <ConfirmationBox
          title="Delete Document"
          content="Are you sure you want to delete document?"
          isOpen={isDeleteConfirmationOpen}
          onClose={onDeleteConfirmatonClose}
          onConfirm={() => {
            if (deletedItem?.file?.s3Url) {
              deleteDocument(deletedItem?.index)
            } else {
              handleDelete(deletedItem?.index)
              onDeleteConfirmatonClose()
            }
          }}
          isLoading={isLoading}
        />
      </Flex>
    )
  }
  return null
}
